import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, SecurityContext, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterLink } from '@angular/router';
import { HelpBlock, HelpImageAnnotation, HelpOfficialSource } from '@core/services/help';
import { HELP_SITE_ORIGIN, urlPublicaDaAjuda } from '@core/services/help/help-site-url';
import {
  HelpErrorFollowup,
  HelpErrorFollowupSource,
  ROTA_NOVO_CHAMADO,
  SUPORTE_WHATSAPP_URL,
  dataIsoDaAjuda,
  formatarDataDaAjuda,
  helpErrorFollowup,
  helpNfseCodeScope,
} from './help-error-followup';

/** Sufixo do id do título "Se o erro continuar": dois painéis abertos não repetem o id. */
let proximoPainel = 0;

@Component({
  selector: 'app-help-content',
  templateUrl: './help-content.component.html',
  styleUrl: './help-content.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
})
export class HelpContentComponent {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly origemKb = inject(HELP_SITE_ORIGIN);
  private readonly router = inject(Router);

  @Input({ required: true }) blocks: HelpBlock[] = [];
  /**
   * `officialSource` do artigo (KB-ERR-03): mostrado no fim, como no site,
   * "Fonte oficial: nome, versão X, de 15 de janeiro de 2026.".
   */
  @Input() officialSource: HelpOfficialSource | null | undefined = null;
  /**
   * Tipo, origem do erro e categoria do artigo: decidem o bloco "Se o erro
   * continuar" (KB-ERR-05), igual ao site. Sem artigo, o bloco não aparece.
   */
  @Input() article: HelpErrorFollowupSource | null | undefined = null;

  /**
   * O link "Abrir chamado sobre esta tela" do bloco "Se o erro continuar"
   * navega dentro do sistema: quem hospeda o painel o fecha ao receber isto.
   */
  @Output() readonly chamadoAberto = new EventEmitter<void>();

  readonly idDoBlocoDeErro = `se-o-erro-continuar-${++proximoPainel}`;
  protected readonly rotaNovoChamado = ROTA_NOVO_CHAMADO;
  protected readonly whatsappDoSuporte = SUPORTE_WHATSAPP_URL;

  blocoDeErro(article: HelpErrorFollowupSource | null | undefined): HelpErrorFollowup | null {
    return article && typeof article.category === 'string' ? helpErrorFollowup(article) : null;
  }

  /** A frase do código nacional (`helpNfseCodeScope` do site), dentro do bloco de erro. */
  escopoDoCodigo(article: HelpErrorFollowupSource | null | undefined): string | null {
    return article && typeof article.category === 'string' ? helpNfseCodeScope(article) : null;
  }

  /** A tela de origem do chamado (`?de=`), sem a query, como no painel "Ajuda e suporte". */
  rotaDeOrigem(): string {
    return this.router.url.split('?')[0];
  }

  aoAbrirChamado(): void {
    this.chamadoAberto.emit();
  }

  /** Data da fonte oficial por extenso (`formatHelpDate` do site); outro formato fica como veio. */
  dataDaFonte(data: string): string {
    return formatarDataDaAjuda(data);
  }

  /** Valor de `datetime`, só quando a data é AAAA-MM-DD válida. */
  dataIsoDaFonte(data: unknown): string | null {
    return dataIsoDaAjuda(data);
  }

  html(bruto: string): string {
    const seguro = this.sanitizer.sanitize(SecurityContext.HTML, bruto) ?? '';
    const documento = new DOMParser().parseFromString(`<div>${seguro}</div>`, 'text/html');
    documento.querySelectorAll('a[href]').forEach(link => {
      const href = link.getAttribute('href') ?? '';
      if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        link.removeAttribute('target');
        link.removeAttribute('rel');
        return;
      }

      try {
        const destino = urlPublicaDaAjuda(href, this.origemKb);
        if (!destino) {
          link.removeAttribute('href');
          return;
        }
        link.setAttribute('href', destino);
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      } catch {
        link.removeAttribute('href');
      }
    });
    const normalizado = documento.body.firstElementChild?.innerHTML ?? '';
    return this.sanitizer.sanitize(SecurityContext.HTML, normalizado) ?? '';
  }

  anotacoes(annotations: unknown): HelpImageAnnotation[] {
    if (!Array.isArray(annotations)) return [];
    return annotations.filter((note): note is HelpImageAnnotation =>
      note !== null && typeof note === 'object' &&
      Number.isFinite(note.x) && note.x >= 0 && note.x <= 100 &&
      Number.isFinite(note.y) && note.y >= 0 && note.y <= 100 &&
      typeof note.text === 'string' && note.text.trim().length > 0,
    );
  }

  /** Link da fonte oficial só em HTTPS absoluto e sem credenciais; senão, a fonte fica como texto. */
  linkDaFonte(url: unknown): string {
    if (typeof url !== 'string') return '';
    try {
      const destino = new URL(url);
      return destino.protocol === 'https:' && !destino.username && !destino.password ? destino.href : '';
    } catch {
      return '';
    }
  }

  imagem(src: string): string {
    return urlPublicaDaAjuda(src, this.origemKb);
  }
}
